import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { theme } from './theme';
import NotificationStreamLabs from './streamLabs/NotificationStreamLabs';
import Toolbar from './components/Toolbar';
import NewsTicker from './components/NewsTicker';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import ChatDock from './components/ChatDock';
import FriendOnlineNotifier from './components/FriendOnlineNotifier';
import DMNotifier from './components/DMNotifier';
import LevelUpNotifier from './components/LevelUpNotifier';
import FriendGroupsDock from './components/FriendGroupsDock';
import FriendsOnlineDock from './components/FriendsOnlineDock';
import MesMessagesDock from './components/MesMessagesDock';
import SocialDock from './components/SocialDock';
import SupportPopup from './components/SupportPopup';
import PresenceActivity from './components/PresenceActivity';
import Canonical from './components/Canonical';
import Seo from './components/Seo';
// Bannière cookies maison retirée : la CMP Google (AdSense, RGPD) gère désormais le consentement
// pub + analytics via Consent Mode (cf public/index.html). Évite la double bannière.
import { AuthProvider } from './context/AuthContext';
import { LibraryProvider } from './context/LibraryContext';
import { PresenceProvider } from './context/PresenceContext';

// Code-splitting : chaque page dans son propre chunk → bundle initial allégé (chargé à la demande).
// lazyRetry : si un chunk est INTROUVABLE (build redéployé → ancien hash 404 côté client mis en cache
// par le service worker), on RECHARGE la page UNE fois pour récupérer l'index.html + main.js à jour.
// Évite l'écran cassé "ChunkLoadError" après chaque déploiement. Le flag de session empêche la boucle.
function lazyRetry(factory) {
  return lazy(() => factory().catch((err) => {
    const KEY = 'vg_chunk_reload';
    if (!sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, '1');
      window.location.reload();
      return new Promise(() => {}); // suspend jusqu'au rechargement
    }
    throw err;
  }));
}

const HomePage = lazyRetry(() => import('./pages/HomePage'));
const StorePage = lazyRetry(() => import('./pages/StorePage'));
const GamePage = lazyRetry(() => import('./pages/GamePage'));
const VideosPage = lazyRetry(() => import('./pages/VideosPage'));
const SupportPage = lazyRetry(() => import('./pages/SupportPage'));
const ChannelsPage = lazyRetry(() => import('./pages/ChannelsPage'));
const ChannelPage = lazyRetry(() => import('./pages/ChannelPage'));
const AboutPage = lazyRetry(() => import('./pages/AboutPage'));
const EmbedPage = lazyRetry(() => import('./pages/EmbedPage'));
const LeaderboardsPage = lazyRetry(() => import('./pages/LeaderboardsPage'));
const AccountPage = lazyRetry(() => import('./pages/AccountPage'));
const CommunityPage = lazyRetry(() => import('./pages/CommunityPage'));
const LobbyPage = lazyRetry(() => import('./pages/LobbyPage'));
const PremiumPage = lazyRetry(() => import('./pages/PremiumPage'));
const PalettesPage = lazyRetry(() => import('./pages/PalettesPage'));

const GlobalStyle = createGlobalStyle`
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  html { scroll-behavior: smooth; overflow-x: hidden; }
  /* Anti-débordement horizontal : les sections (items flex de <main>) doivent pouvoir rétrécir. */
  main { min-width: 0; }

  body {
    font-family: ${({ theme }) => theme.fonts.body};
    background-color: ${({ theme }) => theme.colors.bgPrimary};
    color: ${({ theme }) => theme.colors.textPrimary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    overflow-x: hidden;   /* jamais de scroll horizontal (responsive) : un élément large ne casse plus la page */
  }

  #root {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: ${({ theme }) => theme.colors.bgPrimary}; }
  ::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.colors.primary}; border-radius: 3px; }
  ::selection { background: rgba(124,58,237,0.3); color: ${({ theme }) => theme.colors.textPrimary}; }
`;

// Remet la vue EN HAUT à chaque changement de PAGE (pathname) — sinon on arrive au milieu/en bas de la
// nouvelle page (ex. drawer → « En ligne »). On ignore les changements de query (?tab, ?dm…) qui gèrent
// leur propre scroll dans la page.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { try { window.scrollTo(0, 0); } catch (e) { /* */ } }, [pathname]);
  return null;
}

// Pont de NAVIGATION pour le code HORS Router (ex. les toasts de notification de AuthContext) : ils émettent
// window CustomEvent('vg-navigate', { detail:'/chemin' }) → on navigue en SPA (sans rechargement).
function NavBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    const on = (e) => { try { navigate((e && e.detail) || '/'); } catch (err) { /* */ } };
    window.addEventListener('vg-navigate', on);
    return () => window.removeEventListener('vg-navigate', on);
  }, [navigate]);
  return null;
}

// Détecte l'affichage mobile (< 768px) → sur mobile on ne monte PAS les languettes flottantes (elles sont
// regroupées dans le drawer social) ; sur desktop on ne monte pas le drawer. Évite le double-montage/polling.
function useIsMobile() {
  const q = '(max-width: 767px)';
  const [m, setM] = useState(() => { try { return window.matchMedia(q).matches; } catch (e) { return false; } });
  useEffect(() => {
    let mq; try { mq = window.matchMedia(q); } catch (e) { return undefined; }
    const on = () => setM(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return m;
}

export default function App() {
  const isMobile = useIsMobile();
  // Après 5 s de fonctionnement sain, on lève le garde-fou de rechargement (prêt pour le prochain
  // déploiement). Un chunk RÉELLEMENT manquant échoue avant ces 5 s → pas de boucle de rechargement.
  useEffect(() => {
    const t = setTimeout(() => { try { sessionStorage.removeItem('vg_chunk_reload'); } catch (e) { /* */ } }, 5000);
    return () => clearTimeout(t);
  }, []);
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <AuthProvider>
      <PresenceProvider>
      <LibraryProvider>
      <BrowserRouter>
        <ScrollToTop />
        <NavBridge />
        <NotificationStreamLabs />
        <Toolbar />
        <PresenceActivity />
        <Canonical />
        <Seo />
        <NewsTicker />
        {/* +34px pour dégager la barre de news fixe (sous la toolbar 64px) — les pages gardent leur offset 64px */}
        <main style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingTop: '34px' }}>
          <Suspense fallback={<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', color: theme.colors.textMuted }}>Chargement…</div>}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/store" element={<StorePage />} />
            <Route path="/jeu/:slug" element={<GamePage />} />
            <Route path="/videos" element={<VideosPage />} />
            <Route path="/channels" element={<ChannelsPage />} />
            <Route path="/channels/:key" element={<ChannelPage />} />
            <Route path="/embed" element={<EmbedPage />} />
            <Route path="/communaute" element={<CommunityPage />} />
            <Route path="/classements" element={<LeaderboardsPage />} />
            <Route path="/salons" element={<LobbyPage />} />
            <Route path="/compte" element={<AccountPage />} />
            <Route path="/palettes" element={<PalettesPage />} />
            <Route path="/premium" element={<PremiumPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/support" element={<SupportPage />} />
          </Routes>
          </Suspense>
        </main>
        <Footer />
        <ChatWidget />
        <FriendOnlineNotifier />
        <DMNotifier />
        <LevelUpNotifier />
        {/* Fenêtres de chat DM flottantes (instantané, sans passer par l'espace membre). Desktop ET mobile. */}
        <ChatDock />
        {/* Desktop : languettes flottantes (Amis en ligne / Groupes / Messages). Mobile : drawer social unique. */}
        {!isMobile && <>
          <FriendGroupsDock />
          <FriendsOnlineDock />
          <MesMessagesDock />
        </>}
        {isMobile && <SocialDock />}
        <SupportPopup />
      </BrowserRouter>
      </LibraryProvider>
      </PresenceProvider>
      </AuthProvider>
      <ToastContainer
        position="bottom-right"
        theme="dark"
        toastStyle={{ background: theme.colors.bgCard, border: `1px solid ${theme.colors.border}` }}
      />
    </ThemeProvider>
  );
}
